import React, { useState } from 'react';

const HamburgerButton = ({ 
  isOpen = false, 
  onClick, 
  size = 24, // Fixed default size
  showBorder = true,
  ariaLabel,
  className = '',
  style = {} 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const handleClick = () => {
    if (onClick) {
      setIsClicked(true);
      onClick();
      // Reset click effect after animation
      setTimeout(() => setIsClicked(false), 200);
    }
  };

  // Use fixed size
  const buttonSize = size;
  const borderWidth = Math.max(1, buttonSize * 0.055); // ~2px at 36px size
  const lineWidth = buttonSize * 0.7; // 70% of button size (increased from 60%)
  const lineHeight = Math.max(2, buttonSize * 0.1); // ~3.6px at 36px size (increased from 0.08)
  const lineGap = buttonSize * 0.11; // ~4px at 36px size (increased from 0.083)
  const glowSpread = buttonSize * 0.28; // Proportional glow effect

  // Button container styles with blue border
  const buttonStyles = {
    background: 'transparent',
    border: showBorder ? `${borderWidth}px solid #01a2f1` : 'none',  // Conditional border
    borderRadius: '50%',
    width: `${buttonSize}px`,
    height: `${buttonSize}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    padding: 0,
    outline: 'none',
    position: 'relative',
    boxShadow: showBorder && isHovered ? `0 0 ${glowSpread}px rgba(1, 162, 241, 0.3)` : 'none',  // Conditional shadow
    flexShrink: 0, // Prevent button from shrinking
    ...style
  };

  // Hamburger line styles with GIGiFY orange
  const lineStyles = {
    width: `${lineWidth}px`,
    height: `${lineHeight}px`,
    background: isClicked 
      ? '#ff6b2b' 
      : isHovered 
        ? '#ff9558' 
        : '#f85008', // GIGiFY orange
    borderRadius: `${lineHeight * 0.5}px`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transformOrigin: 'center',
  };

  // Calculate rotation: counter-clockwise 90° when opening (0 to -90)
  // clockwise back when closing (-90 to 0)
  const rotation = isOpen ? -90 : 0;

  return (
    <button
      className={`hamburger-button ${className}`}
      style={buttonStyles}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={ariaLabel || (isOpen ? "Close menu" : "Open menu")}
      aria-expanded={isOpen}
    >
      {/* Container for rotating lines */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${lineGap}px`,
          transform: `rotate(${rotation}deg)`,
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <span style={lineStyles}></span>
        <span style={lineStyles}></span>
        <span style={lineStyles}></span>
      </div>
    </button>
  );
};

// Demo component to show the button in action with different sizes
const Demo = () => {
  const [isOpen1, setIsOpen1] = useState(false);
  const [isOpen2, setIsOpen2] = useState(false);
  const [isOpen3, setIsOpen3] = useState(false);

  return (
    <div style={{ 
      padding: '40px', 
      background: 'linear-gradient(135deg, #144874 0%, #1c1b1f 100%)',
      minHeight: '400px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '30px'
    }}>
      <h2 style={{ color: '#01a2f1', fontFamily: 'system-ui' }}>
        GIGiFY Responsive Hamburger Button
      </h2>
      
      <div style={{ 
        display: 'flex', 
        gap: '40px', 
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        {/* Fixed size examples */}
        <div style={{ textAlign: 'center' }}>
          <HamburgerButton 
            isOpen={isOpen1} 
            onClick={() => setIsOpen1(!isOpen1)}
            size={24}
          />
          <p style={{ color: '#fff', fontSize: '12px', marginTop: '10px' }}>Small (24px)</p>
        </div>
        
        <div style={{ textAlign: 'center' }}>
          <HamburgerButton 
            isOpen={isOpen2} 
            onClick={() => setIsOpen2(!isOpen2)}
            size={48}
          />
          <p style={{ color: '#fff', fontSize: '12px', marginTop: '10px' }}>Medium (48px)</p>
        </div>
        
        <div style={{ textAlign: 'center' }}>
          <HamburgerButton 
            isOpen={isOpen3} 
            onClick={() => setIsOpen3(!isOpen3)}
            size={72}
          />
          <p style={{ color: '#fff', fontSize: '12px', marginTop: '10px' }}>Large (72px)</p>
        </div>
      </div>

      <div style={{
        marginTop: '10px',
        padding: '15px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        color: '#01a2f1',
        fontFamily: 'system-ui',
        fontSize: '12px',
        maxWidth: '500px'
      }}>
        <strong>Features:</strong>
        <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
          <li>Fixed size regardless of viewport</li>
          <li>Set size={'{number}'} for custom pixel size</li>
          <li>All proportions (border, lines, gaps) scale together</li>
          <li>Maintains design integrity at any size</li>
        </ul>
      </div>
    </div>
  );
};

export default HamburgerButton;